import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { CheckCircle2, Download, FileSignature, Loader2, Pencil, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import dayjs from 'dayjs';
import type { ContractorDocument, DocumentTemplateListItem } from '@ip/shared-types';
import { CONTRACTOR_DOC_TYPES } from '@ip/shared-types';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { DatePicker } from '@/components/shared/DatePicker';
import { SignatureBadges } from '@/components/documents/SignatureBadges';
import { useContractorsApi, useDocumentsApi } from '@/services/api';
import { sanitizeFileNamePart } from '@/utils/download';
import { t } from '@/i18n';

const COLUMN_COUNT = 7;

interface ContractorDocumentsProps {
  companyNip: string;
  contractorId: string;
}

function expiryStatus(expiry: string | null): { tag: 'expired' | 'soon' | null; days?: number } {
  if (!expiry) return { tag: null };
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  const exp = new Date(expiry + 'T00:00:00Z');
  const days = Math.round((exp.getTime() - today.getTime()) / 86_400_000);
  if (days < 0) return { tag: 'expired' };
  if (days <= 30) return { tag: 'soon', days };
  return { tag: null, days };
}

function ExpiryCell({ expiryDate }: { expiryDate: string | null }) {
  const s = expiryStatus(expiryDate);
  if (s.tag === 'expired') {
    return (
      <Badge
        variant="outline"
        className="border-red-500/40 bg-red-500/10 font-normal text-red-700 dark:text-red-400"
      >
        {t('contractors.documents.expired')}
      </Badge>
    );
  }
  if (s.tag === 'soon') {
    return (
      <Badge
        variant="outline"
        className="border-amber-500/40 bg-amber-500/10 font-normal text-amber-700 dark:text-amber-400"
      >
        {t('contractors.documents.expiryWarning', { days: s.days! })}
      </Badge>
    );
  }
  return <>{expiryDate ?? '—'}</>;
}

export function ContractorDocuments({ companyNip, contractorId }: ContractorDocumentsProps) {
  const {
    listContractorDocuments,
    addContractorDocument,
    downloadContractorDocument,
    removeContractorDocument,
    updateContractorDocument,
    downloadContractorReturned,
    confirmContractorReturn,
    verifyContractorDocumentSignature,
  } = useContractorsApi();
  const { listTemplates, generateContractorDocument } = useDocumentsApi();
  const [docs, setDocs] = useState<ContractorDocument[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [docType, setDocType] = useState('');
  const [label, setLabel] = useState('');
  const [documentDate, setDocumentDate] = useState<Date | null>(null);
  const [expiry, setExpiry] = useState<Date | null>(null);
  const [note, setNote] = useState('');

  // Metadata edit of an existing row (label/dates/note). The only way to give
  // a generated document an expiry date after the fact — and the reminder
  // ladder restarts server-side when that date changes.
  const [editTarget, setEditTarget] = useState<ContractorDocument | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editDocumentDate, setEditDocumentDate] = useState<Date | null>(null);
  const [editExpiry, setEditExpiry] = useState<Date | null>(null);
  const [editNote, setEditNote] = useState('');
  const [saving, setSaving] = useState(false);

  // Generate-from-template (documents plan 4 task 2/5)
  const [generateOpen, setGenerateOpen] = useState(false);
  const [templates, setTemplates] = useState<DocumentTemplateListItem[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [genTemplateId, setGenTemplateId] = useState('');
  const [genDocType, setGenDocType] = useState('');
  const [genExpiry, setGenExpiry] = useState<Date | null>(null);
  const [generating, setGenerating] = useState(false);

  // Returned-signed-copy confirmation (documents plan 4 task 5)
  const [confirmTarget, setConfirmTarget] = useState<ContractorDocument | null>(null);
  const [confirming, setConfirming] = useState(false);

  // Digital-signature verification of a returned scan (on-demand here).
  const [verifyingDocId, setVerifyingDocId] = useState<string | null>(null);

  const refresh = () => {
    let cancelled = false;
    setLoading(true);
    listContractorDocuments(companyNip, contractorId)
      .then((d) => { if (!cancelled) setDocs(d); })
      .catch((err) => { if (!cancelled) toast.error(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  };

  useEffect(refresh, [companyNip, contractorId, listContractorDocuments]);

  // Only setState calls inside, so the identity is stable for the deep-link
  // effect below (which must open the SAME reset-first dialog as the button).
  const openUpload = useCallback(() => {
    setDocType('');
    setLabel('');
    setDocumentDate(null);
    setExpiry(null);
    setNote('');
    setFile(null);
    setUploadOpen(true);
  }, []);

  // Deep link "dodaj dokument" (`?upload=1`) — used by the CRM account page's
  // "Utwórz kontrahenta i dodaj dokument" CTA, and equally by anyone pasting
  // the URL. It goes through openUpload() so the form is reset exactly like a
  // click on the button (a leftover draft from an earlier upload would
  // otherwise be shown). The param is stripped the moment it is honoured
  // (functional update + `replace`), so a refresh, a back navigation or a tab
  // switch never reopens a dialog the user already closed.
  useEffect(() => {
    if (searchParams.get('upload') !== '1') return;
    openUpload();
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('upload');
        return next;
      },
      { replace: true },
    );
  }, [searchParams, setSearchParams, openUpload]);

  const handleDownload = async (doc: ContractorDocument) => {
    try {
      await downloadContractorDocument(companyNip, contractorId, doc.id, doc.fileName);
    } catch {
      /* session expired — handled by the global modal */
    }
  };

  const handleDelete = async (docId: string) => {
    try {
      await removeContractorDocument(companyNip, contractorId, docId);
      refresh();
    } catch (err) {
      toast.error((err as Error).message);
    }
  };

  const labelMissing = docType === 'inne' && !label.trim();

  const handleUpload = async () => {
    if (!docType) {
      toast.error(t('contractors.documents.typeRequired'));
      return;
    }
    if (!file) {
      toast.error(t('contractors.documents.fileRequired'));
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('docType', docType);
    if (label.trim()) fd.append('label', label.trim());
    if (documentDate) fd.append('documentDate', dayjs(documentDate).format('YYYY-MM-DD'));
    if (expiry) fd.append('expiryDate', dayjs(expiry).format('YYYY-MM-DD'));
    if (note.trim()) fd.append('note', note.trim());
    setBusy(true);
    try {
      await addContractorDocument(companyNip, contractorId, fd);
      setUploadOpen(false);
      setFile(null);
      refresh();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const openGenerate = () => {
    setGenTemplateId('');
    setGenDocType('');
    setGenExpiry(null);
    setGenerateOpen(true);
    setTemplatesLoading(true);
    listTemplates(companyNip, { category: 'contractor' })
      .then((list) => setTemplates(list.filter((tpl) => tpl.currentVersion > 0)))
      .catch((err) => toast.error((err as Error).message))
      .finally(() => setTemplatesLoading(false));
  };

  const handleGenerate = async () => {
    if (!genTemplateId || !genDocType) return;
    setGenerating(true);
    try {
      await generateContractorDocument(companyNip, genTemplateId, {
        contractorId,
        docType: genDocType as ContractorDocument['docType'],
        ...(genExpiry ? { expiryDate: dayjs(genExpiry).format('YYYY-MM-DD') } : {}),
      });
      setGenerateOpen(false);
      toast.success(t('contractors.documents.generateSuccess'));
      refresh();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setGenerating(false);
    }
  };

  const openEdit = (doc: ContractorDocument) => {
    setEditTarget(doc);
    setEditLabel(doc.label ?? '');
    setEditDocumentDate(doc.documentDate ? dayjs(doc.documentDate).toDate() : null);
    setEditExpiry(doc.expiryDate ? dayjs(doc.expiryDate).toDate() : null);
    setEditNote(doc.note ?? '');
  };

  const editLabelMissing = editTarget?.docType === 'inne' && !editLabel.trim();

  // Every field the dialog owns is sent on every save (including as null when
  // cleared) — the PATCH leaves unsent keys alone, and this dialog does know
  // all four of them.
  const handleSaveEdit = async () => {
    if (!editTarget) return;
    setSaving(true);
    try {
      await updateContractorDocument(companyNip, contractorId, editTarget.id, {
        label: editLabel.trim() || null,
        documentDate: editDocumentDate ? dayjs(editDocumentDate).format('YYYY-MM-DD') : null,
        expiryDate: editExpiry ? dayjs(editExpiry).format('YYYY-MM-DD') : null,
        note: editNote.trim() || null,
      });
      setEditTarget(null);
      toast.success(t('contractors.documents.saved'));
      refresh();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  // Returned scan has no stored filename of its own — build one from the
  // document's name; the extension is inferred from the response type.
  const handleDownloadReturned = async (doc: ContractorDocument) => {
    try {
      const base = sanitizeFileNamePart(doc.fileName.replace(/\.[A-Za-z0-9]+$/, ''));
      await downloadContractorReturned(companyNip, contractorId, doc.id, `${base}-podpisany`);
    } catch {
      /* session expired — handled by the global modal */
    }
  };

  const handleVerifySignature = async (docId: string) => {
    setVerifyingDocId(docId);
    try {
      const result = await verifyContractorDocumentSignature(companyNip, contractorId, docId);
      setDocs((prev) => prev.map((d) => (d.id === docId ? { ...d, signatureVerification: result } : d)));
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setVerifyingDocId(null);
    }
  };

  const handleConfirmReturn = async () => {
    if (!confirmTarget) return;
    setConfirming(true);
    try {
      await confirmContractorReturn(companyNip, contractorId, confirmTarget.id);
      toast.success(t('contractors.documents.returns.confirmSuccess'));
      refresh();
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setConfirming(false);
      setConfirmTarget(null);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap gap-2">
        <Button onClick={openUpload}>
          <Plus className="size-4" />
          {t('contractors.documents.upload')}
        </Button>
        <Button variant="outline" onClick={openGenerate}>
          <FileSignature className="size-4" />
          {t('contractors.documents.generateFromTemplate')}
        </Button>
      </div>

      <div className="bg-card overflow-x-auto rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="text-muted-foreground w-[160px] whitespace-nowrap">
                {t('contractors.documents.type')}
              </TableHead>
              <TableHead className="text-muted-foreground whitespace-nowrap">
                {t('contractors.documents.label')}
              </TableHead>
              <TableHead className="text-muted-foreground whitespace-nowrap">
                {t('contractors.documents.fileName')}
              </TableHead>
              <TableHead className="text-muted-foreground w-[140px] whitespace-nowrap">
                {t('contractors.documents.documentDate')}
              </TableHead>
              <TableHead className="text-muted-foreground w-[180px] whitespace-nowrap">
                {t('contractors.documents.expiry')}
              </TableHead>
              <TableHead className="text-muted-foreground whitespace-nowrap">
                {t('contractors.documents.note')}
              </TableHead>
              <TableHead className="w-[180px]" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 3 }, (_, i) => (
                <TableRow key={i}>
                  <TableCell colSpan={COLUMN_COUNT}>
                    <Skeleton className="h-8 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : docs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} className="text-muted-foreground h-24 text-center">
                  {t('common.none')}
                </TableCell>
              </TableRow>
            ) : (
              docs.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="py-2">
                    <Badge variant="secondary" className="font-normal">
                      {t(`contractors.documents.types.${row.docType}`)}
                    </Badge>
                  </TableCell>
                  <TableCell className="py-2">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span>{row.label ?? '—'}</span>
                      {row.hasReturnedFile && (
                        <Badge
                          variant="outline"
                          className={
                            row.returnConfirmed
                              ? 'border-emerald-500/40 bg-emerald-500/10 font-normal text-emerald-700 dark:text-emerald-400'
                              : 'border-sky-500/40 bg-sky-500/10 font-normal text-sky-700 dark:text-sky-400'
                          }
                        >
                          {row.returnConfirmed
                            ? t('contractors.documents.returns.confirmed')
                            : t('contractors.documents.returns.pending')}
                        </Badge>
                      )}
                      <SignatureBadges verification={row.signatureVerification} />
                    </div>
                  </TableCell>
                  <TableCell className="py-2">{row.fileName}</TableCell>
                  <TableCell className="py-2">{row.documentDate ?? '—'}</TableCell>
                  <TableCell className="py-2">
                    <ExpiryCell expiryDate={row.expiryDate} />
                  </TableCell>
                  <TableCell className="max-w-[240px] truncate py-2" title={row.note ?? undefined}>
                    {row.note ?? '—'}
                  </TableCell>
                  <TableCell className="py-2">
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        title={t('contractors.documents.download')}
                        onClick={() => handleDownload(row)}
                      >
                        <Download className="size-4" />
                      </Button>
                      {row.hasReturnedFile && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8"
                          title={t('contractors.documents.returns.downloadReturned')}
                          onClick={() => handleDownloadReturned(row)}
                        >
                          <FileSignature className="size-4" />
                        </Button>
                      )}
                      {row.hasReturnedFile && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8"
                          title={t('signatures.verify')}
                          disabled={verifyingDocId === row.id}
                          onClick={() => handleVerifySignature(row.id)}
                        >
                          {verifyingDocId === row.id ? (
                            <Loader2 className="size-4 animate-spin" />
                          ) : (
                            <ShieldCheck className="size-4" />
                          )}
                        </Button>
                      )}
                      {row.hasReturnedFile && !row.returnConfirmed && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8 text-emerald-600 dark:text-emerald-400"
                          title={t('contractors.documents.returns.confirm')}
                          onClick={() => setConfirmTarget(row)}
                        >
                          <CheckCircle2 className="size-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="icon"
                        className="size-8"
                        title={t('contractors.documents.edit')}
                        onClick={() => openEdit(row)}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button variant="ghost" size="icon" className="text-destructive size-8">
                            <Trash2 className="size-4" />
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>
                              {t('contractors.documents.deleteConfirm')}
                            </AlertDialogTitle>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                            <AlertDialogAction
                              className="bg-destructive text-white hover:bg-destructive/90"
                              onClick={() => handleDelete(row.id)}
                            >
                              {t('common.delete')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={uploadOpen} onOpenChange={setUploadOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('contractors.documents.upload')}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="contractor-doc-type">{t('contractors.documents.type')}</Label>
              <Select value={docType} onValueChange={setDocType}>
                <SelectTrigger id="contractor-doc-type" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTRACTOR_DOC_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`contractors.documents.types.${type}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="contractor-doc-label">
                {t('contractors.documents.label')}
                {docType === 'inne' && <span className="text-destructive"> *</span>}
              </Label>
              <Input
                id="contractor-doc-label"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label>{t('contractors.documents.documentDate')}</Label>
                <DatePicker value={documentDate} onChange={setDocumentDate} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t('contractors.documents.expiry')}</Label>
                <DatePicker value={expiry} onChange={setExpiry} />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="contractor-doc-note">{t('contractors.documents.note')}</Label>
              <Textarea
                id="contractor-doc-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="contractor-doc-file">{t('contractors.documents.fileName')}</Label>
              <input
                id="contractor-doc-file"
                type="file"
                accept="application/pdf,image/*"
                className="text-sm file:mr-3 file:rounded-md file:border file:px-3 file:py-1.5 file:text-sm"
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setUploadOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button disabled={busy || labelMissing} onClick={handleUpload}>
              {busy && <Loader2 className="size-4 animate-spin" />}
              {t('common.add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={generateOpen} onOpenChange={setGenerateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('contractors.documents.generateFromTemplate')}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="contractor-gen-template">{t('contractors.documents.generateTemplate')}</Label>
              {templatesLoading ? (
                <Skeleton className="h-9 w-full" />
              ) : (
                <Select value={genTemplateId} onValueChange={setGenTemplateId}>
                  <SelectTrigger id="contractor-gen-template" className="w-full">
                    <SelectValue placeholder={t('contractors.documents.generateTemplatePlaceholder')} />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.length === 0 ? (
                      <div className="text-muted-foreground px-2 py-1.5 text-sm">
                        {t('contractors.documents.generateNoTemplates')}
                      </div>
                    ) : (
                      templates.map((tpl) => (
                        <SelectItem key={tpl.id} value={tpl.id}>
                          {tpl.name}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="contractor-gen-doctype">{t('contractors.documents.type')}</Label>
              <Select value={genDocType} onValueChange={setGenDocType}>
                <SelectTrigger id="contractor-gen-doctype" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CONTRACTOR_DOC_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {t(`contractors.documents.types.${type}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label>{t('contractors.documents.expiry')}</Label>
              <DatePicker value={genExpiry} onChange={setGenExpiry} />
              <p className="text-muted-foreground text-xs">
                {t('contractors.documents.generateExpiryHint')}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setGenerateOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button disabled={generating || !genTemplateId || !genDocType} onClick={handleGenerate}>
              {generating && <Loader2 className="size-4 animate-spin" />}
              {t('contractors.documents.generateSubmit')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={editTarget !== null}
        onOpenChange={(o) => { if (!o && !saving) setEditTarget(null); }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('contractors.documents.editTitle')}</DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="contractor-doc-edit-label">
                {t('contractors.documents.label')}
                {editTarget?.docType === 'inne' && <span className="text-destructive"> *</span>}
              </Label>
              <Input
                id="contractor-doc-edit-label"
                value={editLabel}
                onChange={(e) => setEditLabel(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="flex flex-col gap-1.5">
                <Label>{t('contractors.documents.documentDate')}</Label>
                <DatePicker value={editDocumentDate} onChange={setEditDocumentDate} />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label>{t('contractors.documents.expiry')}</Label>
                <DatePicker value={editExpiry} onChange={setEditExpiry} />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="contractor-doc-edit-note">{t('contractors.documents.note')}</Label>
              <Textarea
                id="contractor-doc-edit-note"
                value={editNote}
                onChange={(e) => setEditNote(e.target.value)}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" disabled={saving} onClick={() => setEditTarget(null)}>
              {t('common.cancel')}
            </Button>
            <Button disabled={saving || editLabelMissing} onClick={handleSaveEdit}>
              {saving && <Loader2 className="size-4 animate-spin" />}
              {t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmTarget !== null} onOpenChange={(o) => { if (!o && !confirming) setConfirmTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('contractors.documents.returns.confirmTitle')}</AlertDialogTitle>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={confirming}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction disabled={confirming} onClick={handleConfirmReturn}>
              {confirming && <Loader2 className="size-4 animate-spin" />}
              {t('contractors.documents.returns.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
